import { useState } from "react";
import {
  signInWithEmailAndPassword,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
} from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { FiEye, FiEyeOff, FiX } from "react-icons/fi";

import { sendOTP, verifyOTP } from "../services/otpService";
import { resetAdminPassword } from "../services/passwordReset";

export default function Login({ setAdmin }) {
  /* ================= LOGIN STATE ================= */
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);

  /* ================= MODAL STATE ================= */
  const [showForgot, setShowForgot] = useState(false);
  const [showOTP, setShowOTP] = useState(false);

  const [resetEmail, setResetEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);

  /* ================= LOGIN ================= */
  const login = async () => {
    if (!email || !password) return alert("Email and password required");

    try {
      setLoading(true);

      await setPersistence(
        auth,
        remember ? browserLocalPersistence : browserSessionPersistence
      );

      const res = await signInWithEmailAndPassword(
        auth,
        email.trim(),
        password.trim()
      );

      const ref = doc(db, "users", res.user.uid);
      const snap = await getDoc(ref);

      if (!snap.exists()) {
        await setDoc(ref, {
          email: res.user.email,
          role: "user",
          createdAt: serverTimestamp(),
        });
      }

      const finalSnap = await getDoc(ref);

      if (finalSnap.data().role === "admin") {
        setAdmin(true);
      } else {
        await auth.signOut();
        alert("You are not authorized to access the admin panel");
      }

    } catch (err) {
      alert(err.message);
    } finally {
      setLoading(false);
    }
  };

  /* ================= SEND OTP ================= */
  const sendResetOTP = async () => {
    if (!resetEmail) return alert("Enter admin email");

    try {
      setOtpLoading(true);
      await sendOTP(resetEmail);
      setShowForgot(false);
      setShowOTP(true);
    } catch (err) {
      alert(err.message);
    } finally {
      setOtpLoading(false);
    }
  };

  /* ================= VERIFY OTP ================= */
  const verifyResetOTP = async () => {
    if (!otp) return alert("Enter OTP");

    try {
      setOtpLoading(true);
      await verifyOTP(resetEmail, otp);
      await resetAdminPassword(resetEmail);

      alert("Password reset link sent to your email");

      setShowOTP(false);
      setResetEmail("");
      setOtp("");
    } catch (err) {
      alert(err.message);
    } finally {
      setOtpLoading(false);
    }
  };

  const isValid = email.length > 3 && password.length >= 6;

  return (
    <div className="min-h-screen flex items-center justify-center px-4
      bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))]
      from-neutral-800 via-black to-black">

      {/* ===== LOGIN CARD ===== */}
      <div className="
        w-full max-w-sm
        rounded-2xl
        bg-neutral-900/70
        backdrop-blur-xl
        border border-white/10
        shadow-[0_20px_60px_rgba(0,0,0,0.7)]
        p-6
        animate-fadeIn
      ">
        <h2 className="text-xl font-semibold text-center text-white mb-6">
          Admin Access
        </h2>

        {/* EMAIL */}
        <label className="text-sm text-neutral-400">Email</label>
        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full mt-1 mb-4 px-4 py-2.5 rounded-xl
            bg-black/60 border border-neutral-700 text-white
            placeholder-neutral-500 transition
            focus:outline-none focus:border-red-500
            focus:ring-1 focus:ring-red-500/40"
        />

        {/* PASSWORD */}
        <div className="flex items-center justify-between text-sm text-neutral-400">
          <label>Password</label>
          <button
            onClick={() => setShowForgot(true)}
            className="text-red-400 hover:text-red-500 transition"
          >
            Forgot?
          </button>
        </div>

        <div className="relative mt-1 mb-4">
          <input
            type={showPass ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full px-4 py-2.5 rounded-xl
              bg-black/60 border border-neutral-700 text-white
              transition focus:outline-none focus:border-red-500
              focus:ring-1 focus:ring-red-500/40"
          />
          <button
            onClick={() => setShowPass(!showPass)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-white"
          >
            {showPass ? <FiEyeOff /> : <FiEye />}
          </button>
        </div>

        {/* REMEMBER */}
        <label className="flex items-center justify-between text-sm text-neutral-400 mb-5">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={remember}
              onChange={(e) => setRemember(e.target.checked)}
              className="w-4 h-4 rounded border-neutral-600 bg-black accent-red-500"
            />
            <span>Keep me signed in</span>
          </div>
        </label>

        {/* LOGIN BUTTON */}
        <button
          onClick={login}
          disabled={!isValid || loading}
          className="w-full py-3 rounded-xl
            bg-gradient-to-r from-red-500 to-red-600
            text-black font-semibold
            transition-all duration-200
            hover:scale-[1.02] hover:shadow-lg hover:shadow-red-500/30
            active:scale-[0.98]
            disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {loading ? "Signing in..." : "Login →"}
        </button>
      </div>

      {/* ===== FORGOT MODAL ===== */}
      {showForgot && (
        <Modal onClose={() => setShowForgot(false)}>
          <h3 className="text-lg font-semibold mb-4">Forgot Password</h3>
          <input
            placeholder="Admin Email"
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            className="input"
          />
          <PrimaryButton onClick={sendResetOTP} loading={otpLoading}>
            Send OTP →
          </PrimaryButton>
        </Modal>
      )}

      {/* ===== OTP MODAL ===== */}
      {showOTP && (
        <Modal onClose={() => setShowOTP(false)}>
          <h3 className="text-lg font-semibold mb-4">Verify OTP</h3>
          <input
            placeholder="Enter OTP"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            className="input"
          />
          <PrimaryButton onClick={verifyResetOTP} loading={otpLoading}>
            Verify & Send Link →
          </PrimaryButton>
        </Modal>
      )}
    </div>
  );
}

/* ================= MODAL ================= */
function Modal({ children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center
      bg-black/70 backdrop-blur-sm px-4">
      <div className="relative w-full max-w-sm rounded-2xl
        bg-neutral-900/80 backdrop-blur-xl border border-white/10
        p-6 shadow-2xl animate-fadeIn">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-neutral-400 hover:text-white"
        >
          <FiX />
        </button>
        {children}
      </div>
    </div>
  );
}

/* ================= BUTTON ================= */
function PrimaryButton({ children, onClick, loading }) {
  return (
    <button
      onClick={onClick}
      disabled={loading}
      className="w-full mt-4 py-3 rounded-xl
        bg-gradient-to-r from-red-500 to-red-600
        text-black font-semibold
        transition hover:scale-[1.02] active:scale-[0.97]
        disabled:opacity-40"
    >
      {loading ? "Please wait..." : children}
    </button>
  );
}
